<template>
    <TransitionGroup name="floating">
        <FloatingAction
            v-for="action in floatingActions"
            :key="action.label + action.top + action.left"
            :top="action.top"
            :left="action.left"
            :translate="action.translate"
            :disabled="action.disabled"
            :small="action.small"
            @click="action.onClick"
        >
            {{ action.label }}
        </FloatingAction>
    </TransitionGroup>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import FloatingAction from '@/client/ui/context/floating/FloatingAction.vue'
import { useGameBusStore } from '@/client/store/bus.ts'
import { getCardRectangle, getScreenPoint, getScreenScale } from '@/client/game/utils.ts'
import { useGameStateStore } from '@/client/store/gameState.ts'
import { usePlayersStore } from '@/client/state/players.ts'
import { MinionActionType, NO_BLOCK } from '@/shared/types/state.ts'
import { declareAction, startTargetDeclaration } from '@/client/game/declaration.ts'
import { ACTION_TYPES } from '@/shared/const/model.ts'
import { useUIFeatures } from '@/client/game/composables/useUIFeatures.ts'
import { useSelection } from '@/client/game/composables/useSelection.ts'
import { Card, Minion } from '@/shared/model/Card.ts'
import { gameMutations } from '@/shared/state/gameMutations.ts'
import { selfCanAttemptBlock } from '@/client/state/self.ts'
import { getBlockingDecision } from '@/shared/state/actionState.ts'
import { GameType } from '@/shared/types/state.ts'
import { chooseEngineOption, engineCardOptions, engineOptionCards } from '@/client/engine/driver.ts'
import { useEngineStore } from '@/client/store/engine.ts'
import { minionMenuOptions, narrowStaging } from '@/client/engine/interaction.ts'

const gameBus = useGameBusStore()
const gameState = useGameStateStore()
const players = usePlayersStore()
const { actionDeclarationEnabled } = useUIFeatures()
const { singleCard, primedMinion } = useSelection(() => gameBus.selectedCards)

type FloatingActionData = {
    label: string
    top: number
    left: number
    translate?: string
    disabled?: boolean
    small?: boolean
    onClick: VoidFunction
}

const CARD_ACTION_GAP = 10
const CARD_ACTION_HEIGHT = 45
// A small floating action ( see FloatingAction )
const ENGINE_ACTION_HEIGHT = 26

const focusedCard = computed(() => {
    if (gameBus.contextMenu.show || gameBus.declaringTargetOrigin || gameBus.dragAttrs) {
        return
    }
    // With the rules engine : a minion it offers an action, whoever's turn it is ( Enkil Cog )
    if (gameState.gameType == GameType.Engine && primedMinion.value) {
        return primedMinion.value
    }
    if (
        gameState.action ||
        gameState.combat ||
        gameState.referendum ||
        gameState.activePlayer != players.selfPlayer
    ) {
        return
    }

    // Main case : a selected minion capable of performing an action.
    if (primedMinion.value) {
        return primedMinion.value
    }

    // Alternate case : an embrace-like can become a vampire
    if (singleCard.value && singleCard.value.isEmbraceLike()) {
        return singleCard.value
    }

    // Alternate case : a card in play calls a referendum by cardtext
    if (singleCard.value && readyToCallReferendum(singleCard.value)) {
        return singleCard.value
    }

    return undefined
})

// A card is "ready" either via the standard Ready region, or ( Free Table ) via
// a non-flipped presence on the shared table - there is no separate Ready
// region there.
function isCardReady(card: Card): boolean {
    return card.isIn.ready || (gameState.isFreeTable && card.isIn.table && !card.isFlipped)
}

// Cards that call a referendum by cardtext only do it from their place in play.
// A locked one still can : it is not an action it takes.
function readyToCallReferendum(card: Card): boolean {
    return isCardReady(card) && card.canCallReferendum()
}

// Every ready minion of our own can attempt to block the ongoing action, no
// selection needed, when we are an eligible blocker. This is distinct from
// focusedCard, which only lives on our own turn with nothing in progress ( with
// the rules engine : wherever it offers the minion an action, Enkil Cog on
// another's turn ) : a block happens during another player's action, so it has
// its own gating.
const blockingMinions = computed<Minion[]>(() => {
    const selfPlayer = players.selfPlayer
    // With the rules engine : the minions it offers a block with
    if (gameState.gameType == GameType.Engine) {
        return engineBlockers()
    }
    if (
        gameBus.contextMenu.show ||
        gameBus.declaringTargetOrigin ||
        gameBus.dragAttrs ||
        !selfPlayer ||
        !selfCanAttemptBlock()
    ) {
        return []
    }

    // On the shared Free Table there is no separate Ready region : gather our
    // own non-flipped minions straight from the table instead.
    if (gameState.isFreeTable && gameState.table) {
        return gameState.table.cards.filter(
            (c): c is Minion => c.isMinion() && !c.isFlipped && c.controller.oid == selfPlayer.oid,
        )
    }

    return selfPlayer.minionsReady
})

// The minion we are currently attempting the block with, if any. While one is
// set, it is the only minion that keeps a floating action ( to stop the block ).
const selfBlockingMinion = computed<Minion | null>(() => {
    const selfPlayer = players.selfPlayer
    // With the rules engine, a block attempt is not taken back
    if (!selfPlayer || gameState.gameType == GameType.Engine) {
        return null
    }
    const decision = getBlockingDecision(gameState, selfPlayer)
    return decision && decision.block !== NO_BLOCK ? decision.block : null
})

const floatingActions = computed<FloatingActionData[]>(() => {
    const actions: FloatingActionData[] = []

    const card = focusedCard.value
    if (card) {
        // A game with the rules engine : the engine makes the card a vampire ( see engine/projection.ts )
        if (card.isEmbraceLike() && gameState.gameType != GameType.Engine) {
            actions.push(...getEmbraceLikeActions(card))
        }
        // Only a primed minion gets the action buttons, and focusedCard returns
        // it first, so this is that same card
        else if (primedMinion.value) {
            actions.push(...getActingMinionActions(primedMinion.value))
        }

        // Comes on top of whatever the card can do otherwise, minion or not
        if (readyToCallReferendum(card)) {
            actions.push(...getCallReferendumActions(card))
        }
    }

    // Block attempts live outside focusedCard : they happen during another
    // player's action, which focusedCard deliberately excludes, and show under
    // every eligible minion rather than only the selected one. Once a minion is
    // attempting the block, only that one keeps a button ( to stop the block ).
    const blocking = selfBlockingMinion.value
    for (const minion of blockingMinions.value) {
        if (blocking) {
            if (minion.oid == blocking.oid) {
                actions.push(...getBlockActions(minion, true))
            }
        } else {
            actions.push(...getBlockActions(minion, false))
        }
    }

    actions.push(...getEngineActions())

    return actions
})

// A game with the rules engine : its pending options about a card of the table, as buttons under
// that card ( see engineCardOptions ), stacked
function getEngineActions(): FloatingActionData[] {
    if (
        gameState.gameType != GameType.Engine ||
        gameBus.contextMenu.show ||
        gameBus.declaringTargetOrigin ||
        gameBus.dragAttrs
    ) {
        return []
    }
    const actions: FloatingActionData[] = []
    const stacked: Record<string, number> = {}
    for (const { card, option, label } of engineCardOptions()) {
        const positionning = getPositionning(card)
        if (!positionning) {
            continue
        }
        const { x, southActionsTop, cardActionGap } = positionning
        const rank = stacked[card.oid] ?? 0
        stacked[card.oid] = rank + 1
        actions.push({
            label,
            left: x,
            translate: 'translateX(-50%)',
            top:
                southActionsTop +
                rank * (ENGINE_ACTION_HEIGHT * getScreenScale() + cardActionGap / 2),
            small: true,
            onClick: () => chooseEngineOption(option.index),
        })
    }
    return actions
}

// The minions the engine offers a block with, now
function engineBlockers(): Minion[] {
    if (gameBus.contextMenu.show || gameBus.dragAttrs) {
        return []
    }
    return (useEngineStore().pending?.options ?? [])
        .filter(option => option.kind == 'declare_block')
        .map(option => engineOptionCards(option)[0])
        .filter((card): card is Minion => !!card?.isMinion())
}

function getPositionning(card: Card) {
    const scale = getScreenScale()
    const cardActionGap = CARD_ACTION_GAP * scale
    const cardActionHeight = CARD_ACTION_HEIGHT * scale

    // World position of the center of the card
    const worldPoint = gameBus.cardsInGame[card.oid]?.getWorldPosition()

    if (!worldPoint) {
        return null
    }

    const { x, y } = getScreenPoint(worldPoint.x, worldPoint.y)
    const rect = getCardRectangle(card)

    const cardTop = y - (rect.height * scale) / 2
    const cardBottom = y + (rect.height * scale) / 2
    const cardRight = x + (rect.width * scale) / 2
    const cardLeft = x - (rect.width * scale) / 2

    const northActionsTop = cardTop - cardActionHeight - cardActionGap
    const southActionsTop = cardBottom + cardActionGap
    const eastActionsLeft = cardRight + cardActionGap
    const westActionsRight = cardLeft - cardActionGap

    const northActionsTopSmall = northActionsTop + cardActionGap * 2

    return {
        cardActionGap,
        cardActionHeight,
        x,
        y,
        cardTop,
        cardBottom,
        cardRight,
        cardLeft,
        northActionsTop,
        southActionsTop,
        eastActionsLeft,
        westActionsRight,
        northActionsTopSmall,
    }
}

function getActingMinionActions(actingMinion: Minion): FloatingActionData[] {
    if (!actionDeclarationEnabled.value && gameState.gameType != GameType.Engine) {
        return []
    }

    const positionning = getPositionning(actingMinion)
    if (!positionning) {
        return []
    }

    const actions: FloatingActionData[] = []
    const {
        cardActionGap,
        cardActionHeight,
        x,
        cardTop,
        northActionsTop,
        eastActionsLeft,
        westActionsRight,
    } = positionning

    const prey = actingMinion.controller.prey
    const allVampiresInTorpor = Object.values(gameState.players).flatMap(
        player => player.vampiresInTorpor,
    )
    const actionsInHand = actingMinion.controller.hand.cards.filter(card =>
        card.type ? ACTION_TYPES.includes(card.type) : false,
    )
    const cardsInPlay = Object.values(gameState.cards).filter(card => card.isIn.controlled)

    if (isCardReady(actingMinion)) {
        if (prey) {
            actions.push({
                label: 'Bleed',
                left: eastActionsLeft,
                top: cardTop + cardActionHeight + cardActionGap,
                onClick: () => {
                    declareAction({
                        type: MinionActionType.Bleed,
                        actingMinion,
                        target: prey,
                    })
                },
            })
        }

        if (actingMinion.isVampire()) {
            actions.push({
                label: 'Hunt',
                left: westActionsRight,
                top: cardTop + cardActionHeight + cardActionGap,
                translate: 'translateX(-100%)',
                onClick: () => {
                    declareAction({
                        type: MinionActionType.Hunt,
                        actingMinion,
                    })
                },
            })

            actions.push({
                label: 'Diablerize',
                left: eastActionsLeft,
                top: cardTop,
                disabled: allVampiresInTorpor.length == 0,
                onClick: () => {
                    startTargetDeclaration(actingMinion)
                    gameBus.actionDeclaration = {
                        type: MinionActionType.Diablerize,
                        actingMinion,
                        usage: null,
                        validTargets: allVampiresInTorpor,
                        validActionCards: null,
                    }
                },
            })

            actions.push({
                label: 'Rescue',
                left: westActionsRight,
                top: cardTop,
                translate: 'translateX(-100%)',
                disabled: allVampiresInTorpor.length == 0,
                onClick: () => {
                    startTargetDeclaration(actingMinion)
                    gameBus.actionDeclaration = {
                        type: MinionActionType.RescueFromTorpor,
                        actingMinion,
                        usage: null,
                        validTargets: allVampiresInTorpor,
                        validActionCards: null,
                    }
                },
            })
        }

        actions.push({
            label: 'Card In Hand',
            left: x,
            top: northActionsTop,
            disabled: actionsInHand.length == 0,
            onClick: () => {
                gameBus.actionDeclaration = {
                    type: MinionActionType.ActionCardFromHand,
                    actingMinion,
                    usage: null,
                    validTargets: null,
                    validActionCards: actionsInHand,
                }
            },
        })

        actions.push({
            label: 'Card In Play',
            left: x - cardActionGap,
            top: northActionsTop,
            translate: 'translateX(-100%)',
            onClick: () => {
                gameBus.actionDeclaration = {
                    type: MinionActionType.ActionInPlay,
                    actingMinion,
                    usage: null,
                    validTargets: null,
                    validActionCards: cardsInPlay,
                }
            },
        })
    }

    // Vampire in torpor : can only leave torpor.
    if (actingMinion.isIn.torpor) {
        actions.push({
            label: 'Leave Torpor',
            left: x,
            translate: 'translateX(-50%)',
            top: northActionsTop,
            onClick: () => {
                declareAction({
                    type: MinionActionType.LeaveTorpor,
                    actingMinion,
                })
            },
        })
    }

    return gameState.gameType == GameType.Engine ?
            withEngineOptions(actingMinion, actions)
        :   actions
}

/**
 * A game with the rules engine : the menu's entries narrowed to what the engine offers ( see
 * minionMenuOptions ), greyed when it offers nothing ; the actions the menu has no entry for
 * under the minion.
 */
function withEngineOptions(minion: Minion, actions: FloatingActionData[]): FloatingActionData[] {
    const menu = minionMenuOptions(minion)
    const positionning = getPositionning(minion)
    // None while a choice is under way : its buttons would cover the cards to pick
    if (!menu || !positionning || useEngineStore().staging) {
        return []
    }
    const engineActions = actions.map((action): FloatingActionData => {
        const options = menu[action.label] ?? []
        return {
            ...action,
            disabled: options.length == 0,
            onClick: () => narrowStaging(options),
        }
    })
    // With the entries SC's gating didn't build ( a bleed without a prey… ) : no option is out of reach
    const built = new Set(actions.map(action => action.label))
    const other = Object.entries(menu)
        .filter(([label]) => !built.has(label))
        .flatMap(([, options]) => options)
    if (other.length > 0) {
        engineActions.push({
            // A lone one is taken at the click : named by what it does ( Lenelle's Become Anarch )
            label: other.length == 1 ? (other[0].text ?? 'Other Actions') : 'Other Actions',
            left: positionning.x,
            translate: 'translateX(-50%)',
            top: positionning.southActionsTop,
            onClick: () => narrowStaging(other),
        })
    }
    return engineActions
}

function getCallReferendumActions(card: Card): FloatingActionData[] {
    const positionning = getPositionning(card)
    if (!positionning) {
        return []
    }
    const { x, northActionsTop, southActionsTop } = positionning

    return [
        {
            label: 'Start Referendum',
            left: x,
            translate: 'translateX(-50%)',
            // Above the card, like the embrace-like action. A minion already
            // has its action buttons up there, so it takes the free slot below.
            top: card.isMinion() ? southActionsTop : northActionsTop,
            onClick: () => {
                gameMutations.REFERENDUM_call.actSelf({ card })
            },
        },
    ]
}

function getBlockActions(minion: Minion, isBlocking: boolean): FloatingActionData[] {
    if (!actionDeclarationEnabled.value) {
        return []
    }

    const positionning = getPositionning(minion)
    if (!positionning) {
        return []
    }
    const { x, northActionsTopSmall } = positionning
    return [
        {
            label: isBlocking ? 'End Block' : 'Attempt Block',
            left: x,
            translate: 'translateX(-50%)',
            top: northActionsTopSmall,
            small: true,
            onClick: () => {
                gameMutations.ACTION_declareBlock.actSelf({ block: isBlocking ? null : minion })
            },
        },
    ]
}

function getEmbraceLikeActions(embraceLike: Card): FloatingActionData[] {
    const positionning = getPositionning(embraceLike)
    if (!positionning) {
        return []
    }
    const { x, northActionsTopSmall } = positionning
    return [
        {
            label: 'Become Vampire',
            left: x,
            translate: 'translateX(-50%)',
            top: northActionsTopSmall,
            onClick: () => {
                gameMutations.becomeVampire.actSelf({
                    card: embraceLike,
                })
            },
        },
    ]
}
</script>

<style lang="scss">
.floating-enter-active {
    transition: opacity 0.1s ease-out;
    transition-delay: 0.05s;
}

.floating-leave-active {
    transition: opacity 0.1s ease-in;
}

.floating-enter-from,
.floating-leave-to {
    opacity: 0;
}
</style>
